"use client";

import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useFreeOfferEnabled } from "@/contexts/CommerceSettingsContext";

// Helper to check if user has an active team
const hasActiveTeam = (session) => {
    if (!session?.user) return false;

    // Check currentTeam (active or trialing)
    if (
        session.user.currentTeam &&
        (session.user.currentTeam.teamStatus === "active" ||
            session.user.currentTeam.teamStatus === "trialing")
    ) {
        return true;
    }

    // Check availableTeams
    if (session.user.availableTeams?.length > 0) {
        return session.user.availableTeams.some(
            (team) =>
                team.teamStatus === "active" || team.teamStatus === "trialing",
        );
    }

    return false;
};

export default function CourseBannerInfo() {
    const t = useTranslations();
    const { data: session, status } = useSession();
    const [isVisible, setIsVisible] = useState(true);
    // Tout le bandeau promet les premières parties offertes. Offre gratuite
    // fermée dans /admin/promotions, il ne doit plus rien promettre : le
    // hook répond false tant que le serveur n'a pas répondu, donc on ne
    // montre jamais la promesse « au cas où ».
    const freeOfferEnabled = useFreeOfferEnabled();

    useEffect(() => {
        // Vérifier si le bandeau a déjà été fermé
        const bannerClosed = document.cookie
            .split("; ")
            .find((row) => row.startsWith("courseBannerClosed="))
            ?.split("=")[1];

        if (bannerClosed === "true") {
            setIsVisible(false);
        }
    }, []);

    const handleClose = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Fermer le bandeau
        setIsVisible(false);

        // Sauvegarder dans un cookie (expire dans 30 jours)
        const expires = new Date();
        expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000);
        document.cookie = `courseBannerClosed=true; expires=${expires.toUTCString()}; path=/`;
    };

    // Don't show if user is pro or has an active/trialing team
    const hasPremiumAccess = session?.user?.pro || hasActiveTeam(session);

    // Le bandeau s'adressait uniquement aux gens DÉJÀ inscrits (`session &&`),
    // alors qu'il porte l'argument le plus vendeur de la page : les premières
    // parties sont accessibles gratuitement. Un visiteur sans compte le voit
    // désormais, avec un texte qui l'invite à créer un compte plutôt qu'à
    // prendre un abonnement.
    const isGuest = status !== "loading" && !session;

    return (
        !hasPremiumAccess &&
        freeOfferEnabled &&
        isVisible && (
            <div className="sticky top-[65px] w-full bg-confidence p-4 text-white text-sm z-30">
                <div className="relative">
                    <Link
                        href={isGuest ? "/signup" : "/pricing"}
                        className="block text-center hover:underline font-light text-white hover:text-white pr-12"
                    >
                        {t(
                            isGuest
                                ? "CourseBannerInfo_text_guest"
                                : "CourseBannerInfo_text",
                        )}{" "}
                        <b className="font-semibold">
                            {t(
                                isGuest
                                    ? "CourseBannerInfo_button_guest"
                                    : "CourseBannerInfo_button",
                            )}
                        </b>
                    </Link>
                    <button
                        onClick={handleClose}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                        aria-label={t("CourseBannerInfo_close")}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
            </div>
        )
    );
}
